// @ts-check
// =============================================================================
// js/screens/duel/log.js — the battle log: narration + spectator count + chat.
//
// WHAT THIS IS
//   The running battle narration PLUS in-duel chat, in one scrolling DOM panel
//   above the PIXI canvas. The server sends each turn's `duel.log` as an HTML
//   string with `[cardId]` tokens; styleLog swaps those for hoverable card
//   names that press-hold zoom. Chat messages (paid duelists + spectators —
//   see net/socket.sendChat) interleave into the same stream, tagged so a
//   "hide chat" toggle can remove them (and the input) at once.
//
// HEADER
//   Title, a live spectator count (when anyone is watching), and the hide/show
//   chat toggle. A paid viewer also gets a message input pinned to the bottom.
//
// WHY DOM, NOT PIXI (unchanged): the log is server-authored rich HTML; the DOM
//   overlay gives faithful markup + scrolling + text selection for free.
// =============================================================================

import { store } from '../../core/store.js';
import { can } from '../../app/session.js';
import { sendChat, setChatHandler } from '../../net/socket.js';
import { openModal } from '../../ui/modal.js';

/** The interleaved stream, newest first. Each: {kind:'log',html} | {kind:'chat',nickname,message,mine}. */
let entries = /** @type {Array<any>} */ ([]);

/** Which duel the entries belong to. Entries survive REMOUNTS of the same
 *  duel (a lobby detour, a reconnect) but must never leak into a NEW game:
 *  a rematch entered via a lobby join/host/bot RESPONSE mounts this screen
 *  fresh while the module state still holds the previous game's log + chat
 *  (the socket path's new-id clear never runs for response-delivered duels). */
let entriesDuelId = /** @type {any} */ (null);

/** Keep memory bounded on very long games. */
const MAX_ENTRIES = 250;

/** Open/closed state persists across duels within the session. */
let logOpen = window.matchMedia('(min-width: 1100px)').matches;

/** Chat hidden? Persists within the session. */
let chatHidden = false;

/** @type {HTMLElement|null} */
let panelEl = null;
/** @type {HTMLElement|null} */
let toggleEl = null;
/** @type {HTMLElement|null} */
let messagesEl = null;
/** @type {HTMLElement|null} */
let specEl = null;
/** @type {HTMLElement|null} */
let chatToggleEl = null;

/** The zoom hook (set by the duel screen: card object -> show big card). */
let zoomCard = /** @type {(card: any) => void} */ (() => {});

/**
 * Mount the log overlay + toggle button into the duel stage element.
 * @param {HTMLElement} stageEl          The #duel-stage container.
 * @param {(card: any) => void} onZoom   Press-hold zoom handler for card tokens.
 * @returns {void}
 */
export function mountLog(stageEl, onZoom) {
	zoomCard = onZoom;

	// A different duel's history must not carry over (the "log persisted
	// into the rematch" report); the same duel keeps its lines on remount.
	syncEntriesToDuel();

	toggleEl = document.createElement('button');
	toggleEl.className = 'duel-log-toggle';
	toggleEl.title = 'Toggle the action log';
	toggleEl.textContent = '📜 Log';
	toggleEl.addEventListener('click', () => {
		logOpen = !logOpen;
		syncVisibility();
	});
	stageEl.appendChild(toggleEl);

	panelEl = document.createElement('div');
	panelEl.className = 'duel-log';
	buildPanel();
	stageEl.appendChild(panelEl);

	// Receive chat from the server for as long as the log is mounted.
	setChatHandler((payload) => addChat(payload));

	// Mobile keyboard: the stage is a fixed 100dvh that does NOT shrink when
	// the on-screen keyboard opens — only the VISUAL viewport does — so the
	// panel (and its pinned chat input) sank behind the keys. Track the
	// visual viewport and lift the panel's bottom above the covered strip.
	window.visualViewport?.addEventListener('resize', applyKeyboardInset);
	window.visualViewport?.addEventListener('scroll', applyKeyboardInset);

	renderMessages();
	refreshChrome();
	syncVisibility();
}

/**
 * Shrink the log to the keyboard-free part of the screen. The covered strip
 * is the gap between the layout viewport and the visual viewport; with no
 * keyboard (or on desktop) it's 0 and the panel keeps its normal 8px inset.
 * @returns {void}
 */
function applyKeyboardInset() {
	if (!panelEl) return;
	const vv = window.visualViewport;
	const covered = vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0;
	panelEl.style.bottom = (8 + covered) + 'px';
}

/** Remove the DOM (duel screen unmounting). */
export function unmountLog() {
	window.visualViewport?.removeEventListener('resize', applyKeyboardInset);
	window.visualViewport?.removeEventListener('scroll', applyKeyboardInset);
	setChatHandler(null);
	panelEl?.remove();
	toggleEl?.remove();
	panelEl = null;
	toggleEl = null;
	messagesEl = null;
	specEl = null;
	chatToggleEl = null;
}

/** Wipe the log (a new duel started — old client :6777). */
export function clearLog() {
	entries = [];
	entriesDuelId = null; // next write adopts the live duel (syncEntriesToDuel)
	renderMessages();
}

/**
 * Prepend a new server log line (newest first, like the old client :6774).
 * @param {string} logHtml  The raw duel.log HTML from the server.
 * @returns {void}
 */
export function appendLog(logHtml) {
	if (!logHtml) return;
	pushEntry({ kind: 'log', html: styleLog(logHtml) });
	renderMessages();
	refreshChrome();
}

/** Is the log currently open? (The duel layout may reserve side space.) */
export function isLogOpen() {
	return logOpen;
}

/**
 * Refresh the header chrome (spectator count) — the duel screen calls this on
 * every state change so the count stays live even between log lines.
 * @returns {void}
 */
export function refreshChrome() {
	if (!specEl) return;
	const count = store.duel?.spectatorCount || 0;
	if (count > 0) {
		specEl.textContent = `👁 ${count} watching`;
		specEl.style.display = '';
	} else {
		specEl.style.display = 'none';
	}
}

/**
 * The viewer list popup ("N watching" chip). Names come pre-resolved from the
 * server (spectatorNames — nicknames only, never ids); built with textContent
 * so a mischievous nickname can't inject markup.
 * @returns {void}
 */
function showSpectatorList() {
	const names = store.duel?.spectatorNames || [];
	const body = document.createElement('div');
	body.style.cssText = 'text-align:left;line-height:1.8;max-height:50dvh;overflow-y:auto;';
	if (names.length) {
		for (const name of names) {
			const row = document.createElement('div');
			row.textContent = '👁 ' + name;
			body.appendChild(row);
		}
	} else {
		body.textContent = 'Nobody is watching right now.';
	}
	openModal({ title: 'Judgy Spectators', body, buttons: [{ label: 'Close' }] });
}

// -----------------------------------------------------------------------------
// Internals
// -----------------------------------------------------------------------------

/** Add a chat message to the stream (from the server). */
function addChat(payload) {
	if (!payload || !payload.message) return;
	pushEntry({
		kind: 'chat',
		nickname: String(payload.nickname || 'Duelist'),
		message: String(payload.message),
		mine: payload.id && payload.id === store.userData?.id,
	});
	renderMessages();
}

/**
 * Adopt the live duel: when the entries belong to a DIFFERENT game, wipe
 * them first. Self-healing at every write and mount, so no caller ordering
 * (response-delivered rematch, socket-path switch, remount) can leak one
 * game's lines into the next. A null live duel adopts nothing — pre-duel
 * chat has nowhere to render anyway.
 */
function syncEntriesToDuel() {
	const duelId = store.duel?.id ?? null;
	if (duelId !== entriesDuelId) {
		entries = [];
		entriesDuelId = duelId;
	}
}

/** Prepend an entry and cap the buffer. */
function pushEntry(entry) {
	syncEntriesToDuel();
	entries.unshift(entry);
	if (entries.length > MAX_ENTRIES) entries.length = MAX_ENTRIES;
}

/** Build the persistent panel shell once (header, messages, chat input). */
function buildPanel() {
	if (!panelEl) return;
	panelEl.innerHTML = '';

	const header = document.createElement('div');
	header.className = 'duel-log-header';

	const title = document.createElement('span');
	title.className = 'duel-log-title';
	title.textContent = 'Battle Log';

	specEl = document.createElement('span');
	specEl.className = 'duel-log-spectators';
	specEl.style.display = 'none';
	// The chip is a button: tap to see WHO is watching (server sends display
	// nicknames alongside the count — see duel-view.js).
	specEl.style.cursor = 'pointer';
	specEl.title = "See who's watching";
	specEl.addEventListener('click', showSpectatorList);

	chatToggleEl = document.createElement('button');
	chatToggleEl.className = 'duel-log-chat-toggle';
	chatToggleEl.addEventListener('click', () => {
		chatHidden = !chatHidden;
		applyChatHidden();
	});

	header.append(title, specEl, chatToggleEl);
	panelEl.appendChild(header);

	messagesEl = document.createElement('div');
	messagesEl.className = 'duel-log-messages';
	panelEl.appendChild(messagesEl);

	// Paid viewers (players or spectators) get a message input.
	if (can('chat')) {
		panelEl.appendChild(buildChatInput());
	}

	applyChatHidden();
}

/** The bottom chat compose row (paid only). */
function buildChatInput() {
	const row = document.createElement('div');
	row.className = 'duel-log-chat-input';

	const input = document.createElement('input');
	input.type = 'text';
	input.maxLength = 140;
	input.placeholder = 'Say something…';
	input.className = 'duel-chat-field';

	const send = document.createElement('button');
	send.className = 'defaultButton duel-chat-send';
	send.textContent = 'Send';

	const submit = () => {
		const text = input.value.trim();
		if (!text) return;
		sendChat(text);
		input.value = '';
		input.focus();
	};
	send.addEventListener('click', submit);
	input.addEventListener('keydown', (e) => {
		if (e.key === 'Enter') { e.preventDefault(); submit(); }
	});

	row.append(input, send);
	return row;
}

/** Reflect the chat-hidden state on the panel + toggle label. */
function applyChatHidden() {
	panelEl?.classList.toggle('chat-hidden', chatHidden);
	if (chatToggleEl) {
		chatToggleEl.textContent = chatHidden ? '💬 Show chat' : '💬 Hide chat';
		chatToggleEl.classList.toggle('is-active', !chatHidden);
	}
}

/** Apply open/closed + platform classes. */
function syncVisibility() {
	if (!panelEl) return;
	panelEl.classList.toggle('is-open', logOpen);
	toggleEl?.classList.toggle('is-active', logOpen);
}

/** Paint the accumulated stream (log lines + chat), newest first. */
function renderMessages() {
	if (!messagesEl) return;
	messagesEl.innerHTML = '';

	for (const entry of entries) {
		const row = document.createElement('div');
		if (entry.kind === 'chat') {
			// Chat is USER input -> textContent only, never innerHTML.
			row.className = 'duel-log-entry duel-log-chat-entry' + (entry.mine ? ' is-mine' : '');
			const name = document.createElement('span');
			name.className = 'duel-chat-name';
			name.textContent = entry.nickname + ': ';
			const msg = document.createElement('span');
			msg.className = 'duel-chat-text';
			msg.textContent = entry.message;
			row.append(name, msg);
		} else {
			// Server-authored narration HTML (card tokens etc.) — safe to render.
			row.className = 'duel-log-entry';
			row.innerHTML = entry.html;
		}
		messagesEl.appendChild(row);
	}

	// Wire press-hold zoom on every card token (styleLog marks them).
	for (const token of messagesEl.querySelectorAll('[data-card-id]')) {
		const id = Number(token.getAttribute('data-card-id'));
		const card = store.cards[id];
		if (!card) continue;
		let holdTimer = 0;
		const start = () => { holdTimer = window.setTimeout(() => zoomCard(card), 250); };
		const stop = () => window.clearTimeout(holdTimer);
		token.addEventListener('mousedown', start);
		token.addEventListener('mouseup', stop);
		token.addEventListener('mouseleave', stop);
		token.addEventListener('touchstart', start, { passive: true });
		token.addEventListener('touchend', stop);
	}
}

/**
 * Replace "[cardId]" tokens with marked-up card names (old StyleLog :7050 +
 * LogCardAdder :7057, minus the inline event handlers — we bind real
 * listeners in renderMessages instead).
 * @param {string} entry
 * @returns {string}
 */
function styleLog(entry) {
	let out = String(entry);
	let guard = 0;
	while (out.includes('[') && out.includes(']') && guard++ < 50) {
		const token = out.split('[')[1].split(']')[0];
		const id = Number(token);
		const card = store.cards[id];
		if (!card) {
			out = out.replace('[' + token + ']', token);
			continue;
		}
		const title = String(card.text || '').replaceAll('<br>', ' - ').replaceAll('"', '&quot;');
		out = out.replace(
			'[' + token + ']',
			`<b><u><span class="duel-log-card" data-card-id="${id}" title="${title}">${card.name}</span></u></b>`,
		);
	}
	return out;
}
